import { useContext, useMemo, useRef } from 'react';
import _get from 'lodash/get';
import _snakeCase from 'lodash/snakeCase';
import { useQuery, useSubscription } from '@apollo/client';
import { csvClient } from '../lib/ApolloClient';
import queryDefs from '../lib/queryDefs';
import useRefetchOnVisibility from '../lib/useRefetchOnVisibility';
import useSaleTableState from '../lib/useSaleTableState';
import Alert from './Alert';
import CollectionContext from './CollectionContext';
import Meta from './Meta';
import SaleTable from './SaleTable';
import SaleTableActions from './SaleTableActions';
import SaleTableRows from './SaleTableRows';
import SaleTableToolbar from './SaleTableToolbar';
import { TLDsContext } from './App';
import SaleTableFetchMoreButton from './SaleTableFetchMoreButton';
import { SaleTableFilterConfigs } from './SaleTable.columns';

/**
 * SaleTablePage component for displaying paginated sale data in a table format
 *
 * @param {Object} props - Component props
 * @param {string|null} [props.csvDownloadUrl=null] - URL for CSV download functionality
 * @param {React.ReactNode} [props.emptyResultsDescription] - Description shown when no results are found
 * @param {React.ReactNode} [props.emptyResultsTitle] - Title shown when no results are found
 * @param {Array} [props.filterConfigs=SaleTableFilterConfigs] - Configuration for table filters
 * @param {Object} props.query - GraphQL query object for fetching data
 * @param {Object|null} [props.queryCsv=null] - GraphQL query object for CSV export
 * @param {Object|null} [props.queryVars=null] - Additional variables for GraphQL queries
 * @param {number} [props.pageSize=100] - Number of items to display per page
 * @param {number} [props.pollInterval] - Polling interval for query refetching in milliseconds
 * @param {boolean} [props.refetchOnVisibility=false] - Whether to refetch data when component becomes visible
 * @param {Function} [props.rowFilter] - Filter function for table rows, receives row object and returns boolean
 * @param {string} [props.salesPath='sales'] - Path to sales data in GraphQL response
 * @param {(data: any) => boolean} [props.showAddToCart] - Function or boolean to determine if add to cart should be shown
 * @param {(data: any) => boolean} [props.showWatch] - Function or boolean to determine if watch functionality should be shown
 * @param {string} [props.title=''] - Page title
 * @param {(data: any) => any[]} [props.visibleColumns] - Function or array defining which columns should be visible
 * @param {Object} [props.defaultTableState] - Default state for the table
 * @returns {React.ReactElement} The rendered SaleTablePage component
 */
export default function SaleTablePage({
  csvDownloadUrl = null,
  emptyResultsDescription = (
    <>
      We weren’t able to find any domains matching the specified criteria. Try
      again using different search filters.
    </>
  ),
  emptyResultsTitle = <>No domains found.</>,
  filterConfigs = SaleTableFilterConfigs,
  query,
  queryCsv = null,
  queryVars = null,
  pageSize = 100,
  pollInterval = undefined,
  refetchOnVisibility = false,
  rowFilter = row => true,
  salesPath = 'sales',
  showAddToCart = data => false,
  showWatch = data => true,
  title = '',
  visibleColumns = data => [],
  ...props
}) {
  const scrollParent = useRef();

  const defaultTableState = useMemo(
    () => ({
      ...props.defaultTableState,
    }),
    [props.defaultTableState], // This prop must be memoized!
  );

  const tlds = useContext(TLDsContext);

  const state = useSaleTableState(defaultTableState);

  const pageQuery = useQuery(query, {
    pollInterval,
    fetchPolicy: 'cache-first', // This is important for pagination to work when navigation back to this page
    variables: {
      ...state.api,
      ...queryVars,
      page: 1,
      pageSize,
      filter: {
        ...(queryVars?.filter ?? {}),
        ...state.api.filter,
        // NOTE: Reducing TLD to a string fixes an issue where data wasn't updating. Maybe try using useLazyQuery instead?
        tld:
          state.api.filter?.tld?.length > 0
            ? state.api.filter.tld.join(',')
            : undefined,
      },
    },
  });

  useRefetchOnVisibility(
    pageQuery,
    refetchOnVisibility && pageQuery.data?.sales.page === 1,
  );

  const { data, error, loading, fetchMore, variables } = pageQuery;

  const csvQuery = useQuery(queryCsv || query, {
    client: csvClient,
    fetchPolicy: 'no-cache',
    skip: true,
    variables,
  });

  const getRows = result =>
    result.items
      ?.filter(sale => (tlds ? tlds.has(sale.product.tld) : true))
      .filter(rowFilter) ?? [];

  // Resolve data
  const heading = data?.collection?.heading ?? title;
  const result = _get(data, salesPath, {});
  const currentPage = _get(data, `${salesPath}.page`, 1);
  const currentPageSize = _get(data, `${salesPath}.pageSize`, pageSize);
  const rows = getRows(result);

  const hasReachedEnd =
    result.items && result.items?.length < currentPage * currentPageSize;

  const auctionIds = rows
    .filter(sale => sale.saleType === 'auction')
    .map(sale => sale.id);

  useSubscription(queryDefs.saleUpdated, {
    variables: { saleIds: auctionIds },
    skip: !auctionIds.length,
  });

  if (!data && error) {
    return (
      <div className="tw-px-4 tw-my-4">
        <div className="tw-mx-auto tw-max-w-screen-2xl">
          <Alert severity="error" title="Failed to load data" />
        </div>
      </div>
    );
  }

  if (!loading && !_get(data, salesPath)) {
    return (
      <div className="tw-px-4 tw-my-4">
        <div className="tw-mx-auto tw-max-w-screen-2xl">
          <Alert severity="warning" title="Page not found">
            The page at {window?.location.pathname ?? ''} was not found.
          </Alert>
        </div>
      </div>
    );
  }

  return (
    <>
      {data?.collection && (
        <Meta
          description={data.collection.subheading}
          robots="all"
          title={`${data.collection.heading} | Domain Name Auctions - Namecheap`}
        />
      )}
      <div className="tw-px-4">
        <div className="tw-mx-auto tw-max-w-screen-2xl">
          <h1 className="tw-text-2xl md:tw-text-3xl tw-text-gray-700 tw-font-bold tw-pt-6">
            {heading}
          </h1>
        </div>
      </div>
      <CollectionContext.Provider
        value={data?.collection?.visibility === 'public' && data.collection}>
        <SaleTable
          defaultState={defaultTableState}
          initialState={state}
          loading={loading}
          sales={rows}
          total={result.total}
          visibleColumns={
            typeof visibleColumns === 'function'
              ? visibleColumns(data)
              : visibleColumns
          }>
          <div className="tw-px-4">
            <div className="tw-mx-auto tw-max-w-screen-2xl">
              <div className="tw-border-b tw-border-gray-100">
                <SaleTableToolbar filterConfigs={filterConfigs} />
              </div>
            </div>
          </div>
          <div className="md:tw-px-4" ref={scrollParent}>
            <div className="md:tw-mx-auto md:tw-max-w-screen-2xl">
              <SaleTableActions
                className="tw-pl-1"
                showAddToCart={
                  typeof showAddToCart === 'function'
                    ? showAddToCart(data)
                    : showAddToCart
                }
                csvDownloadUrl={csvDownloadUrl}
                csvFilename={_snakeCase(heading)}
                onGetCsvPage={async (page, pageSize) => {
                  const { data } = await csvQuery.fetchMore({
                    variables: {
                      page,
                      pageSize,
                    },
                  });
                  const result = _get(data, salesPath, {});
                  return getRows(result);
                }}
              />
              <div className="md:tw-paper tw-overflow-hidden">
                <SaleTableRows
                  showWatch={
                    typeof showWatch === 'function'
                      ? showWatch(data)
                      : showWatch
                  }
                />
                {result.items?.length === 0 && (
                  <>
                    <div className="tw-text-center tw-py-16 tw-px-4">
                      <div className="tw-text-lg tw-font-bold">
                        {emptyResultsTitle}
                      </div>
                      <div className="tw-text-sm lg:tw-text-base">
                        {emptyResultsDescription}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
          <div className="tw-px-4 tw-my-3">
            <div className="tw-mx-auto tw-max-w-screen-2xl">
              {result.items?.length > 0 && !hasReachedEnd && (
                <SaleTableFetchMoreButton
                  onClick={async () => {
                    await fetchMore({ variables: { page: currentPage + 1 } });
                  }}
                />
              )}
            </div>
          </div>
        </SaleTable>
      </CollectionContext.Provider>
    </>
  );
}
